import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Copy, MessageCircle, Plus, RefreshCw, Search, Send, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { whatsappAPI } from '../../../config/whatsapp-api';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { WHATSAPP_SESSION_BLOCKED_MSG } from '../../../constants/whatsappSession';
import { helpers } from '../../../utils/helpers';
import './WhatsAppChat.css';

const PAGE_SIZE = 50;
const AUTO_REFRESH_MS = 60 * 1000;
const RECENT_UNREAD_MS = 3 * 60 * 1000;
const ALERT_SOUND = '/alerts/new_msg_alert.mp3';

const MESSAGE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'incoming', label: 'Messages' },
  { id: 'feedback', label: 'Feedback' },
];

const formatPhone = (phone = '') => {
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('974')) return `+${digits}`;
  if (digits.length === 8) return `+974${digits}`;
  return `+${digits}`;
};

const normalizePhone = (phone = '') => {
  let digits = String(phone).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 8) digits = `974${digits}`;
  return digits;
};

const formatTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const WhatsAppChat = ({ adminData, initialPhone = '' }) => {
  const canView = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_WHATSAPP_CHAT);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_WHATSAPP_CHAT);

  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(normalizePhone(initialPhone) || null);
  const [thread, setThread] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(Boolean(normalizePhone(initialPhone)));
  const [loadingList, setLoadingList] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [messageFilter, setMessageFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [composerText, setComposerText] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewChatPanel, setShowNewChatPanel] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState('');
  const [newChatName, setNewChatName] = useState('');
  const [newChatMessage, setNewChatMessage] = useState('');

  const messagesEndRef = useRef(null);
  const selectedPhoneRef = useRef(selectedPhone);
  const unreadSnapshotRef = useRef({});
  const threadMessagesCountRef = useRef(0);
  const conversationsInitializedRef = useRef(false);
  const lastAlertAtRef = useRef(0);
  selectedPhoneRef.current = selectedPhone;

  const playNewMessageAlert = () => {
    const now = Date.now();
    if (now - lastAlertAtRef.current < 4000) return;
    lastAlertAtRef.current = now;
    const audio = new Audio(ALERT_SOUND);
    audio.play().catch(() => {});
  };

  const isRecentIncomingUnread = (chat) => {
    const unread = chat?.unreadCount || 0;
    if (unread <= 0 || chat?.lastDirection !== 'incoming') return false;
    const lastAt = new Date(chat?.lastMessageAt).getTime();
    return !Number.isNaN(lastAt) && (Date.now() - lastAt) <= RECENT_UNREAD_MS;
  };

  const checkConversationsForNewUnread = (list = []) => {
    if (!conversationsInitializedRef.current) {
      conversationsInitializedRef.current = true;
      const snapshot = {};
      list.forEach((chat) => {
        snapshot[normalizePhone(chat.userPhoneNumber)] = chat.unreadCount || 0;
      });
      unreadSnapshotRef.current = snapshot;
      return;
    }

    let shouldPlay = false;
    const nextSnapshot = {};

    list.forEach((chat) => {
      const phone = normalizePhone(chat.userPhoneNumber);
      const unread = chat.unreadCount || 0;
      nextSnapshot[phone] = unread;

      if (isRecentIncomingUnread(chat)) {
        const previousUnread = unreadSnapshotRef.current[phone] || 0;
        if (unread > previousUnread) {
          shouldPlay = true;
        }
      }
    });

    unreadSnapshotRef.current = nextSnapshot;
    if (shouldPlay) playNewMessageAlert();
  };

  const checkThreadForNewUnread = (messages = []) => {
    const previousCount = threadMessagesCountRef.current;
    if (messages.length > previousCount) {
      const addedMessages = messages.slice(previousCount);
      const hasRecentIncoming = addedMessages.some((message) => (
        message.direction === 'incoming'
        && !message.isRead
        && !Number.isNaN(new Date(message.timestamp).getTime())
        && (Date.now() - new Date(message.timestamp).getTime()) <= RECENT_UNREAD_MS
      ));
      if (hasRecentIncoming) playNewMessageAlert();
    }
    threadMessagesCountRef.current = messages.length;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = useCallback(async (page = 1, searchTerm = search, filter = messageFilter, silent = false) => {
    try {
      if (!silent) setLoadingList(true);
      const response = await whatsappAPI.fetchConversations({
        page,
        limit: PAGE_SIZE,
        search: searchTerm,
        filter,
      });

      if (response.success) {
        const nextConversations = response.data || [];
        if (silent) {
          checkConversationsForNewUnread(nextConversations);
        } else {
          conversationsInitializedRef.current = false;
        }
        setConversations(nextConversations);
        setPagination({
          currentPage: response.pagination?.currentPage || page,
          totalPages: response.pagination?.totalPages || 1,
          totalCount: response.pagination?.totalCount || 0,
          hasNext: Boolean(response.pagination?.hasNext),
          hasPrev: Boolean(response.pagination?.hasPrev),
        });
      } else if (!silent) {
        toast.error(response.msg || 'Failed to load conversations');
        setConversations([]);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      if (!silent) {
        toast.error('Error loading conversations');
        setConversations([]);
      }
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, [search, messageFilter]);

  const fetchThread = useCallback(async (phone, { markRead = true, silent = false } = {}) => {
    const normalized = normalizePhone(phone);
    if (!normalized) return;

    try {
      if (!silent) setLoadingThread(true);
      const response = await whatsappAPI.fetchThread(normalized);
      if (response.success) {
        const nextThread = response.data || null;
        if (silent && nextThread?.messages) {
          checkThreadForNewUnread(nextThread.messages);
        } else if (nextThread?.messages) {
          threadMessagesCountRef.current = nextThread.messages.length;
        } else {
          threadMessagesCountRef.current = 0;
        }
        setThread(nextThread);
        if (markRead && (response.data?.unreadCount || 0) > 0) {
          await whatsappAPI.markConversationRead(normalized);
          setConversations((prev) =>
            prev.map((item) =>
              normalizePhone(item.userPhoneNumber) === normalized
                ? { ...item, unreadCount: 0 }
                : item
            )
          );
          setThread((prev) => (prev ? { ...prev, unreadCount: 0 } : prev));
        }
      } else if (!silent) {
        toast.error(response.msg || 'Failed to load chat');
      }
    } catch (error) {
      console.error('Error loading thread:', error);
      if (!silent) toast.error('Error loading chat');
    } finally {
      if (!silent) setLoadingThread(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) return;
    fetchConversations(currentPage, search, messageFilter);
  }, [canView, currentPage, search, messageFilter, fetchConversations]);

  useEffect(() => {
    if (!canView || !selectedPhone) return;
    threadMessagesCountRef.current = 0;
    fetchThread(selectedPhone, { markRead: true });
  }, [canView, selectedPhone, fetchThread]);

  useEffect(() => {
    scrollToBottom();
  }, [thread?.messages?.length, selectedPhone]);

  useEffect(() => {
    if (!canView) return undefined;

    const timer = setInterval(() => {
      fetchConversations(currentPage, search, messageFilter, true);
      if (selectedPhoneRef.current) {
        fetchThread(selectedPhoneRef.current, { markRead: false, silent: true });
      }
    }, AUTO_REFRESH_MS);

    return () => clearInterval(timer);
  }, [canView, currentPage, search, messageFilter, fetchConversations, fetchThread]);

  const openConversation = (phone) => {
    const normalized = normalizePhone(phone);
    setSelectedPhone(normalized);
    setMobileDetailOpen(true);
  };

  const closeMobileDetail = () => {
    setMobileDetailOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearch(searchInput.trim());
  };

  const handleFilterChange = (nextFilter) => {
    if (nextFilter === messageFilter) return;
    setCurrentPage(1);
    setMessageFilter(nextFilter);
  };

  const handleCopyNumber = async (phone) => {
    const value = formatPhone(phone) || phone;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Number copied');
    } catch (error) {
      toast.error('Failed to copy number');
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!canEdit || !selectedPhone || !composerText.trim() || sending) return;

    if (thread?.canSendFreeForm === false) {
      toast.error(WHATSAPP_SESSION_BLOCKED_MSG);
      return;
    }

    try {
      setSending(true);
      const response = await whatsappAPI.sendChatMessage({
        phoneNumber: selectedPhone,
        message: composerText.trim(),
        chatId: thread?.lastReplyChatId || '',
        userName: thread?.userName || '',
      });

      if (response.success) {
        setComposerText('');
        await Promise.all([
          fetchThread(selectedPhone, { markRead: false, silent: true }),
          fetchConversations(currentPage, search, messageFilter, true),
        ]);
      } else {
        toast.error(response.msg || 'Failed to send message');
      }
    } catch (error) {
      console.error('Send error:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStartNewChat = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    const phone = normalizePhone(newChatPhone);
    const message = newChatMessage.trim();
    if (!phone) {
      toast.error('Enter a valid phone number');
      return;
    }
    if (!message) {
      toast.error('Enter a message');
      return;
    }

    try {
      setSending(true);
      const response = await whatsappAPI.sendChatMessage({
        phoneNumber: phone,
        message,
        userName: newChatName.trim(),
      });

      if (response.success) {
        toast.success('Message sent');
        setShowNewChatPanel(false);
        setNewChatPhone('');
        setNewChatName('');
        setNewChatMessage('');
        setSelectedPhone(phone);
        setMobileDetailOpen(true);
        setCurrentPage(1);
        await fetchConversations(1, search, messageFilter, true);
        await fetchThread(phone, { markRead: true });
      } else {
        toast.error(response.msg || 'Failed to start chat');
      }
    } catch (error) {
      console.error('New chat error:', error);
      toast.error('Failed to start chat');
    } finally {
      setSending(false);
    }
  };

  if (!canView) {
    return (
      <div className="wa-chat-page">
        <div className="wa-chat-empty">You do not have permission to view WhatsApp chats.</div>
      </div>
    );
  }

  const messages = thread?.messages || [];
  const canSendFreeForm = thread?.canSendFreeForm === true;

  return (
    <div className="wa-chat-page">
      <div className={`wa-chat-layout ${mobileDetailOpen && selectedPhone ? 'mobile-detail-open' : ''}`}>
        <aside className="wa-chat-sidebar">
          <div className="wa-chat-sidebar-header">
            <div className="wa-chat-sidebar-title">
              <MessageCircle size={18} />
              <div>
                <h1>WhatsApp Chat</h1>
                <p>Send & receive</p>
              </div>
            </div>
            <div className="wa-chat-sidebar-actions">
              {canEdit && (
                <button
                  type="button"
                  className="wa-chat-refresh-btn"
                  onClick={() => setShowNewChatPanel(true)}
                  title="New chat"
                >
                  <Plus size={16} />
                </button>
              )}
              <button
                type="button"
                className="wa-chat-refresh-btn"
                onClick={() => {
                  fetchConversations(currentPage, search, messageFilter);
                  if (selectedPhone) fetchThread(selectedPhone, { markRead: false });
                }}
                disabled={loadingList}
                title="Refresh"
              >
                <RefreshCw size={16} className={loadingList ? 'spinning' : ''} />
              </button>
            </div>
          </div>

          <form className="wa-chat-search" onSubmit={handleSearch}>
            <Search size={16} />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, number, message"
            />
          </form>

          <div className="wa-chat-filters">
            {MESSAGE_FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`wa-chat-filter-btn ${messageFilter === item.id ? 'active' : ''}`}
                onClick={() => handleFilterChange(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="wa-chat-list">
            {loadingList ? (
              <div className="wa-chat-empty">Loading chats...</div>
            ) : conversations.length === 0 ? (
              <div className="wa-chat-empty">No conversations found</div>
            ) : (
              conversations.map((chat) => {
                const phone = formatPhone(chat.userPhoneNumber) || chat.userPhoneNumber || '';
                const name = chat.userName || phone || 'Unknown';
                const isSelected = normalizePhone(selectedPhone) === normalizePhone(chat.userPhoneNumber);
                const unread = chat.unreadCount || 0;

                return (
                  <div
                    key={chat.userPhoneNumber}
                    className={`wa-chat-item ${isSelected ? 'selected' : ''} ${unread ? 'unread' : ''}`}
                    onClick={() => openConversation(chat.userPhoneNumber)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openConversation(chat.userPhoneNumber);
                      }
                    }}
                  >
                    <div className="wa-chat-avatar">{name.charAt(0).toUpperCase()}</div>
                    <div className="wa-chat-item-body">
                      <div className="wa-chat-item-top">
                        <span className="wa-chat-name">{name}</span>
                        <span className="wa-chat-time">{formatTime(chat.lastMessageAt)}</span>
                      </div>
                      <div className="wa-chat-phone-row">
                        <span className="wa-chat-phone">{phone || 'No number'}</span>
                        {unread > 0 && <span className="wa-chat-unread-badge">{unread}</span>}
                      </div>
                      <p className="wa-chat-preview">
                        {chat.lastDirection === 'outgoing' ? 'You: ' : ''}
                        {chat.lastPreview || 'No messages'}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="wa-chat-pagination">
            <button
              type="button"
              disabled={!pagination.hasPrev || loadingList}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </button>
            <span>
              Page {pagination.currentPage} of {pagination.totalPages}
              {pagination.totalCount ? ` · ${pagination.totalCount}` : ''}
            </span>
            <button
              type="button"
              disabled={!pagination.hasNext || loadingList}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        </aside>

        <section className="wa-chat-panel">
          {selectedPhone ? (
            <div className="wa-chat-detail">
              <div className="wa-chat-detail-header">
                <div className="wa-chat-detail-user">
                  <button
                    type="button"
                    className="wa-chat-back-btn"
                    onClick={closeMobileDetail}
                    title="Back to chats"
                    aria-label="Back to chats"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <div className="wa-chat-avatar">
                    {(thread?.userName || selectedPhone || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="wa-chat-detail-user-text">
                    <h2>{thread?.userName || formatPhone(selectedPhone)}</h2>
                    <p>
                      {formatPhone(selectedPhone)}
                      <button
                        type="button"
                        className="wa-chat-icon-btn"
                        title="Copy number"
                        onClick={() => handleCopyNumber(selectedPhone)}
                      >
                        <Copy size={14} />
                      </button>
                    </p>
                  </div>
                </div>
              </div>

              <div className="wa-chat-detail-body wa-chat-thread-body">
                {loadingThread && messages.length === 0 ? (
                  <div className="wa-chat-empty">Loading messages...</div>
                ) : messages.length === 0 ? (
                  <div className="wa-chat-empty">No messages yet. Send the first message below.</div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={`${msg.direction}-${msg._id}`}
                      className={`wa-chat-bubble-row ${msg.direction === 'outgoing' ? 'outgoing' : 'incoming'}`}
                    >
                      <div className={`wa-chat-bubble ${msg.direction === 'outgoing' ? 'outgoing' : ''}`}>
                        {msg.type === 'feedback' && <span className="wa-chat-type-badge feedback">Feedback</span>}
                        <p className="wa-chat-bubble-text">{msg.text}</p>
                        {Array.isArray(msg.selectedOptions) && msg.selectedOptions.length > 0 && (
                          <ul className="wa-chat-feedback-options">
                            {msg.selectedOptions.map((option) => (
                              <li key={option}>{option}</li>
                            ))}
                          </ul>
                        )}
                        <span className="wa-chat-bubble-time">
                          {msg.direction === 'outgoing' && msg.adminName ? `${msg.adminName} · ` : ''}
                          {formatTime(msg.timestamp)}
                          {msg.direction === 'incoming' && !msg.isRead ? ' · Unread' : ''}
                          {msg.direction === 'outgoing' && msg.status === 'failed' ? ' · Failed' : ''}
                        </span>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {canEdit ? (
                <>
                  {!canSendFreeForm && !loadingThread && (
                    <div className="wa-chat-session-warning">
                      {WHATSAPP_SESSION_BLOCKED_MSG}
                    </div>
                  )}
                  <form className="wa-chat-composer" onSubmit={handleSend}>
                    <input
                      type="text"
                      value={composerText}
                      onChange={(e) => setComposerText(e.target.value)}
                      placeholder={canSendFreeForm ? 'Type a message' : 'Reply window closed (24 hours)'}
                      disabled={sending || !canSendFreeForm}
                    />
                    <button type="submit" disabled={sending || !composerText.trim() || !canSendFreeForm} title="Send">
                      <Send size={16} />
                    </button>
                  </form>
                </>
              ) : (
                <div className="wa-chat-composer-disabled">You need edit permission to send messages.</div>
              )}
            </div>
          ) : (
            <div className="wa-chat-panel-empty">
              <MessageCircle size={42} />
              <h2>WhatsApp conversations</h2>
              <p>Select a chat on the left, or start a new chat.</p>
              {canEdit && (
                <button type="button" className="wa-chat-new-chat-cta" onClick={() => setShowNewChatPanel(true)}>
                  <Plus size={16} /> New chat
                </button>
              )}
            </div>
          )}
        </section>
      </div>

      {showNewChatPanel && canEdit && (
        <div className="wa-chat-side-panel-overlay" onClick={() => !sending && setShowNewChatPanel(false)}>
          <aside className="wa-chat-side-panel" onClick={(e) => e.stopPropagation()}>
            <div className="wa-chat-side-panel-header">
              <h2>New chat</h2>
              <button type="button" className="wa-chat-icon-btn" onClick={() => setShowNewChatPanel(false)} disabled={sending}>
                <X size={18} />
              </button>
            </div>
            <form className="wa-chat-side-panel-form" onSubmit={handleStartNewChat}>
              <label>
                Phone number
                <input
                  type="text"
                  value={newChatPhone}
                  onChange={(e) => setNewChatPhone(e.target.value)}
                  placeholder="50123456 or 97450123456"
                  required
                />
              </label>
              <label>
                Name (optional)
                <input
                  type="text"
                  value={newChatName}
                  onChange={(e) => setNewChatName(e.target.value)}
                  placeholder="Customer name"
                />
              </label>
              <label>
                Message
                <textarea
                  value={newChatMessage}
                  onChange={(e) => setNewChatMessage(e.target.value)}
                  placeholder="Type the first message"
                  rows={5}
                  required
                />
              </label>
              <p className="wa-chat-session-note">
                Free-form messages only work within 24 hours of the customer&apos;s last message. Otherwise use a WhatsApp template.
              </p>
              <button type="submit" className="wa-chat-side-panel-submit" disabled={sending}>
                {sending ? 'Sending...' : 'Send & open chat'}
              </button>
            </form>
          </aside>
        </div>
      )}
    </div>
  );
};

export default WhatsAppChat;
